/**
 * The package catalogue: a service filter, a grid of packages, the editor and
 * the delete confirmation.
 *
 * Two lives, one component. On the packages tab it lists the whole catalogue
 * behind a service filter. Embedded in a service detail view
 * (`lockedProductCode`) the filter goes away - it would be a control with one
 * possible answer - and a new package silently belongs to that service.
 *
 * It draws NO page heading in either life, which is a deliberate departure from
 * the shared `PackagesManager` and its `showHeader`. `PlansSection` already
 * renders the page's `PageHead`, and the tab immediately above this says
 * "Paketid"; adding a second heading would put two `<h1>`s on one page and name
 * the same section twice, two lines apart.
 *
 * Everything below the chrome is `usePlansCatalog` from `@wa/ui` - the same
 * fetch, the same filter arithmetic and the same translated delete errors that
 * web_admin runs on.
 */
import { useState } from "react";
import { Loader2, Package, Plus } from "lucide-react";
import { localeText, makeT, usePlansCatalog, type Locale, type PlanDTO } from "@wa/ui";
import { useI18n } from "../../../../i18n/I18nProvider";
import { api } from "../../../../lib/api";
import { Btn, Card, EmptyState, PillTabs, Toolbar, ToolbarGap } from "../../../ui";
import { ConfirmModal } from "../../_shared/ConfirmModal";
import { PlanCard } from "./PlanCard";
import { PlanEditorModal } from "./PlanEditorModal";

/** The "every service" pill. `usePlansCatalog` spells that state `null`; PillTabs
 *  needs a string, so the empty string stands in and is mapped back on change. */
const ALL = "";

export function PlansPanel({ lockedProductCode }: { lockedProductCode?: string }) {
  const { locale } = useI18n();
  // The catalogue speaks et/en only. The CRM interface language may one day be
  // something else, and `localeText` falls back on its own, but the hook's
  // signature is narrower than the provider's string.
  const loc: Locale = locale === "en" ? "en" : "et";
  const t = makeT(loc);
  const c = usePlansCatalog({ api, locale: loc, lockedProductCode });
  const [editing, setEditing] = useState<PlanDTO | "new" | null>(null);

  const newBtn = (
    <Btn tone="brand" onClick={() => setEditing("new")}>
      <Plus className="size-4" />
      {t("packages.new")}
    </Btn>
  );

  return (
    <div>
      {/* Filter on the left, create on the right - the toolbar shape every list
          screen in the CRM uses. Embedded there is no filter, so the button
          stands alone against the right edge and the row still reads the same. */}
      <Toolbar>
        {c.showPills ? (
          <PillTabs
            value={c.productFilter ?? ALL}
            onChange={(v) => c.setProductFilter(v === ALL ? null : v)}
            tabs={[
              { value: ALL, label: t("products.all"), count: c.allPlans.length },
              ...c.products.map((p) => ({
                value: p.code,
                label: localeText(p.name, loc),
                count: c.planCountFor(p.code),
              })),
            ]}
          />
        ) : null}
        <ToolbarGap />
        {newBtn}
      </Toolbar>

      {c.loading ? (
        <Card className="flex justify-center py-16 text-muted-foreground">
          <Loader2 className="size-6 animate-spin" />
        </Card>
      ) : c.plans.length === 0 ? (
        <Card>
          <EmptyState icon={Package} title={t("packages.empty")} body={t("packages.subtitle")} action={newBtn} />
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {c.plans.map((p) => (
            <PlanCard
              key={p.code}
              plan={p}
              product={c.productByCode.get(p.productCode)}
              showProduct={c.showProductOnCards}
              locale={loc}
              t={t}
              onEdit={() => setEditing(p)}
              onDelete={() => c.askDelete(p)}
            />
          ))}
        </div>
      )}

      {editing ? (
        // Keyed so that opening a second package rebuilds the form. `usePlanForm`
        // seeds every field in a `useState` initialiser, which runs once per
        // mount - reusing the instance would show the first package's values.
        <PlanEditorModal
          key={editing === "new" ? "new" : editing.code}
          api={api}
          locale={loc}
          catalog={c.catalog}
          monitorCatalog={c.monitorCatalog}
          products={c.products}
          defaultProductCode={c.newPlanProduct}
          lockedProductCode={lockedProductCode}
          plan={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void c.reload();
          }}
        />
      ) : null}

      {c.deleteTarget ? (
        // A refused delete keeps the dialog open with the reason in place of the
        // package name: "subscriptions use this package" and "the default plan
        // can't be deleted" are the two answers an operator actually needs, and
        // closing on failure would throw both away.
        <ConfirmModal
          title={t("editor.deleteConfirm")}
          body={c.deleteErr ?? localeText(c.deleteTarget.name, loc)}
          confirmLabel={t("delete")}
          cancelLabel={t("cancel")}
          onConfirm={c.doDelete}
          onClose={() => c.setDeleteTarget(null)}
        />
      ) : null}
    </div>
  );
}
